import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { integrationsService } from '@/services/integrations';
import type { PolicyBatchKey, PolicyBatchPatch } from '@/services/integrations';
import { STALE_TIME_FIVE_MINUTES_MS } from '@/shared/constants/query-stale-time.constants';

/** Chave da listagem — toda escrita no balde a invalida. */
const POLICY_BATCH_QUERY_KEY = ['policy-batch'];
/** Chave dos comboboxes (valores distintos). */
export const POLICY_BATCH_FACETS_QUERY_KEY = ['policy-batch-facets'];
/** Chave dos cards de saldo por pool. */
export const POLICY_BATCH_SUMMARY_QUERY_KEY = ['policy-batch-summary'];

/**
 * Saldo por pool (ramo + origem) para os cards da tela, com o mínimo configurado
 * no BFF. Toda escrita no balde invalida — ver `onSuccess` das mutations abaixo.
 */
export function usePolicyBatchSummary() {
  return useQuery({
    queryKey: POLICY_BATCH_SUMMARY_QUERY_KEY,
    queryFn: async () => {
      const response = await integrationsService.getPolicyBatchSummary();
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Erro ao carregar o saldo do balde');
      }
      return response.data;
    },
    staleTime: STALE_TIME_FIVE_MINUTES_MS,
  });
}

/**
 * Valores distintos do balde (ramos, origens de emissão, nº de balde) para os comboboxes.
 * Vêm do BFF em vez de enum chumbado: produto novo aparece sozinho.
 */
export function usePolicyBatchFacets() {
  return useQuery({
    queryKey: POLICY_BATCH_FACETS_QUERY_KEY,
    queryFn: async () => {
      const response = await integrationsService.getPolicyBatchFacets();
      if (!response.success || !response.data) {
        throw new Error(
          response.error || 'Erro ao carregar os filtros do balde'
        );
      }
      return response.data;
    },
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * As mutations NÃO lançam em erro de negócio: devolvem o envelope `{ success, error }`
 * para o modal de confirmação exibir a mensagem inline (ex.: "já está sendo utilizada no
 * i4Pro") sem se fechar e perder o que o usuário preencheu.
 */
export function useUpdatePolicyBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      key,
      patch,
    }: {
      key: PolicyBatchKey;
      patch: PolicyBatchPatch;
    }) => integrationsService.updatePolicyBatchItem(key, patch),
    onSuccess: result => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: POLICY_BATCH_QUERY_KEY });
        queryClient.invalidateQueries({
          queryKey: POLICY_BATCH_FACETS_QUERY_KEY,
        });
        queryClient.invalidateQueries({
          queryKey: POLICY_BATCH_SUMMARY_QUERY_KEY,
        });
      }
    },
  });
}

export function useDeletePolicyBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (key: PolicyBatchKey) =>
      integrationsService.deletePolicyBatchItem(key),
    onSuccess: result => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: POLICY_BATCH_QUERY_KEY });
        queryClient.invalidateQueries({
          queryKey: POLICY_BATCH_FACETS_QUERY_KEY,
        });
        queryClient.invalidateQueries({
          queryKey: POLICY_BATCH_SUMMARY_QUERY_KEY,
        });
      }
    },
  });
}

export function useBulkUpdatePolicyBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      items,
      patch,
    }: {
      items: PolicyBatchKey[];
      patch: PolicyBatchPatch;
    }) => integrationsService.bulkUpdatePolicyBatch(items, patch),
    onSuccess: result => {
      // Lote parcial ainda mexeu na base: invalida mesmo com `failed > 0`.
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: POLICY_BATCH_QUERY_KEY });
        queryClient.invalidateQueries({
          queryKey: POLICY_BATCH_FACETS_QUERY_KEY,
        });
        queryClient.invalidateQueries({
          queryKey: POLICY_BATCH_SUMMARY_QUERY_KEY,
        });
      }
    },
  });
}

/**
 * Pré-checagem da liberação em lote. É uma mutation e não uma query porque depende da
 * seleção do momento e roda sob demanda, no clique de Salvar — não deve ser cacheada nem
 * refeita sozinha: cada execução consulta o i4Pro item a item.
 */
export function useCheckPolicyBatchRelease() {
  return useMutation({
    mutationFn: (items: PolicyBatchKey[]) =>
      integrationsService.checkPolicyBatchRelease(items),
  });
}
